import { Link } from 'react-router-dom';
import {
  WHATSAPP_1,
  WHATSAPP_1_DISPLAY,
  WHATSAPP_2,
  WHATSAPP_2_DISPLAY,
  STORE_EMAIL,
  STORE_ADDRESS_LINE1,
  STORE_ADDRESS_LINE2,
  STORE_MAPS_URL,
  STORE_HOURS_WEEKDAY,
  STORE_HOURS_SATURDAY,
} from '../../constants/contact';
import { WhatsAppIcon, EmailIcon, PinIcon, ClockIcon } from '../ui/ContactIcons';
import { HeartIcon } from '../ui/HeartIcon';
import { useConsentStore } from '../../store/consentStore';

function InstagramIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
    </svg>
  );
}

export function Footer() {
  const resetConsent = useConsentStore((s) => s.reset);

  return (
    <footer className="bg-champagne/80 text-text-dark mt-16 border-t border-champagne-dark">
      {/* Corpo do rodapé */}
      <div className="max-w-6xl mx-auto px-4 py-10 md:py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Coluna 1 — Marca */}
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="font-brand text-3xl text-chocolate-dark leading-none">Baby Chic</h2>
            <p className="text-sm text-text-medium mt-2 leading-relaxed">
              Vestimos criança como criança!
            </p>
          </div>
          <a
            href={`https://www.instagram.com/babychic.sm/`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 min-h-[44px] text-sm text-text-medium hover:text-chocolate-dark transition-colors group"
          >
            <span className="w-9 h-9 rounded-full bg-white border border-champagne-dark text-chocolate-dark flex items-center justify-center group-hover:bg-chocolate-dark group-hover:text-white group-hover:border-chocolate transition-colors">
              <InstagramIcon />
            </span>
            @babychic.sm
          </a>
        </div>

        {/* Coluna 2 — Contato */}
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-bold text-text-dark">Contato</h3>
          <ul className="flex flex-col gap-0 sm:gap-3">
            <li>
              <a
                href={`https://wa.me/${WHATSAPP_1}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-text-medium hover:text-chocolate-dark transition-colors"
              >
                <span className="text-olivine-dark"><WhatsAppIcon /></span>
                {WHATSAPP_1_DISPLAY}
              </a>
            </li>
            <li>
              <a
                href={`https://wa.me/${WHATSAPP_2}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-text-medium hover:text-chocolate-dark transition-colors"
              >
                <span className="text-olivine-dark"><WhatsAppIcon /></span>
                {WHATSAPP_2_DISPLAY}
              </a>
            </li>
            <li>
              <a
                href={`mailto:${STORE_EMAIL}`}
                className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm text-text-medium hover:text-chocolate-dark transition-colors"
              >
                <EmailIcon />
                {STORE_EMAIL}
              </a>
            </li>
          </ul>
        </div>

        {/* Coluna 3 — Endereço */}
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-bold text-text-dark">Onde estamos</h3>
          <a
            href={STORE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-2 min-h-[44px] sm:min-h-0 py-2 sm:py-0 text-sm text-text-medium hover:text-chocolate-dark transition-colors"
          >
            <PinIcon />
            <span>
              {STORE_ADDRESS_LINE1}<br />
              {STORE_ADDRESS_LINE2}
            </span>
          </a>
          <div className="flex items-start gap-2 text-sm text-text-medium">
            <ClockIcon />
            <span>
              {STORE_HOURS_WEEKDAY}<br />
              {STORE_HOURS_SATURDAY}
            </span>
          </div>
        </div>

        {/* Coluna 4 — Links úteis */}
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-bold text-text-dark">Links úteis</h3>
          <ul className="flex flex-col gap-0 sm:gap-2">
            {[
              { label: 'Início', href: '/' },
              { label: 'Sobre nós', href: '/sobre' },
              { label: 'Meus pedidos', href: '/pedidos' },
              { label: 'Meus dados', href: '/perfil' },
              { label: 'Minha sacola', href: '/cart' },
              { label: 'Trocas e devoluções', href: '/sobre#trocas-e-devolucoes' },
              { label: 'Cadastrar', href: '/cadastro' },
            ].map(({ label, href }) => (
              <li key={href}>
                <Link
                  to={href}
                  className="flex items-center min-h-[44px] sm:min-h-0 text-sm text-text-medium hover:text-chocolate-dark transition-colors"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Divider + copyright */}
      <div className="border-t border-champagne-dark">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-text-medium">
            © {new Date().getFullYear()} Baby Chic. Todos os direitos reservados.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 text-xs">
            <Link to="/privacidade" className="inline-flex items-center min-h-[44px] sm:min-h-0 text-text-medium hover:text-chocolate-dark transition-colors underline-offset-2 hover:underline">
              Política de privacidade
            </Link>
            <button
              type="button"
              onClick={resetConsent}
              className="inline-flex items-center min-h-[44px] sm:min-h-0 text-text-medium hover:text-chocolate-dark transition-colors underline-offset-2 hover:underline"
            >
              Preferências de cookies
            </button>
          </div>
          <p className="text-xs text-text-medium inline-flex items-center gap-1">
            Moda infantil com amor <HeartIcon filled className="w-3.5 h-3.5" />
          </p>
        </div>
      </div>
    </footer>
  );
}
